/**
 * @spec docs/specs/features/staking-position-ui.md#design-system
 * @figma https://www.figma.com/design/Z2CmEcX1brcgv3hfhOWO07/Phenom-Poker?node-id=26237-88329
 *
 * Application header: logo, primary navigation, and wallet/auth status.
 *
 * Mobile: Logo + Wallet + Menu button (nav lives in the drawer)
 * Desktop: Logo + full nav + Play + Wallet
 */
import { useState, useCallback, useMemo, lazy, Suspense } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { cn } from '@/lib/cn';
import { routes } from '@/lib/routes';
import { ConnectWalletButton } from '@/components/wallet/ConnectWalletButton';
import { WalletStatus } from '@/components/wallet/WalletStatus';
import { NetworkPill } from '@/components/wallet/NetworkPill';
const MultiWalletDrawer = lazy(() =>
  import('@/components/wallet/MultiWalletDrawer').then((m) => ({ default: m.MultiWalletDrawer }))
);
import { SignInButton } from '@/components/auth/SignInButton';
import { Icon } from '@/components/ui/icons';
import { MobileMenu } from './MobileMenu';
import { getNavItems, PLAY_URL } from './nav';
import { NavBadge } from './NavBadge';
import { useWalletAddress } from '@/hooks/useWalletAddress';
import { useAuth } from '@/hooks/useAuth';
// TODO(playing-wallet-removal): Delete these imports once Biconomy removal is confirmed successful.
// Soft-removed 2026-03-18 per monday.com #2777499320
// import { usePlayingWallet } from '@/hooks/usePlayingWallet';
// import { useUSDTBalancePublic } from '@/hooks/useTokenBalance';
import Logo from '@/assets/images/logo.svg';
import { useSelectedWallet } from '@/hooks/useSelectedWallet';
import type { DrawerView } from '@/components/wallet/MultiWalletDrawer';

/**
 * Menu button for mobile
 */
function MenuButton({ isOpen, onClick }: { isOpen: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'lg:hidden inline-flex size-10 items-center justify-center rounded-control',
        'text-text-secondary ring-1 ring-inset ring-white/[0.08] hover:bg-white/[0.06] hover:text-text-primary',
        'transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-300'
      )}
      aria-label={isOpen ? 'Close menu' : 'Open menu'}
      aria-expanded={isOpen}
    >
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path
          d={isOpen ? 'M6 6l12 12M18 6L6 18' : 'M4 7h16M4 12h16M4 17h10'}
          stroke="currentColor"
          strokeWidth="1.75"
          strokeLinecap="round"
        />
      </svg>
    </button>
  );
}

/**
 * Application header with logo, navigation, and wallet/auth connection
 */
export function Header() {
  const location = useLocation();
  const navigate = useNavigate();
  const { isConnected } = useWalletAddress();
  const { isAuthenticated, email, isWalletOnlyMode, walletAddress } = useAuth();
  const { address: selectedAddress, setSelectedAddress } = useSelectedWallet();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isWalletDrawerOpen, setIsWalletDrawerOpen] = useState(false);
  const [drawerInitialView, setDrawerInitialView] = useState<DrawerView | undefined>();

  // TODO(playing-wallet-removal): Delete this block once Biconomy removal is confirmed successful.
  // Soft-removed 2026-03-18 per monday.com #2777499320
  const privyAddress = !isWalletOnlyMode && isAuthenticated ? walletAddress : null;

  const hasSession = isConnected || isAuthenticated;
  const navItems = useMemo(
    () => getNavItems(hasSession ? selectedAddress : null),
    [hasSession, selectedAddress]
  );

  const handleCloseMobileMenu = useCallback(() => {
    setIsMobileMenuOpen(false);
  }, []);

  const handleToggleMobileMenu = useCallback(() => {
    setIsMobileMenuOpen((prev) => !prev);
  }, []);

  // When switching wallets on the portfolio page, navigate to the new address
  const handleSelectWallet = useCallback(
    (address: string) => {
      setSelectedAddress(address);
      if (location.pathname.startsWith('/address/')) {
        navigate(routes.address(address));
      }
    },
    [setSelectedAddress, location.pathname, navigate]
  );

  const handleOpenWalletDrawer = useCallback(() => {
    setDrawerInitialView(undefined);
    setIsWalletDrawerOpen(true);
  }, []);

  const handleCloseWalletDrawer = useCallback(() => {
    setIsWalletDrawerOpen(false);
  }, []);

  return (
    <>
      <header className="sticky top-0 z-40 border-b border-white/[0.06] bg-bg-primary/70 backdrop-blur-xl supports-[backdrop-filter]:bg-bg-primary/55">
        <div className="mx-auto flex h-16 max-w-7xl items-center gap-2 px-4 sm:gap-4 md:px-6">
          {/* Logo */}
          <Link
            to={routes.home()}
            className="flex shrink-0 items-center rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-300"
          >
            <img src={Logo} alt="Phenom Poker" className="h-6 w-auto lg:h-7" />
          </Link>

          {/* Desktop navigation */}
          <nav aria-label="Primary" className="hidden flex-1 items-center justify-center lg:flex">
            <ul className="flex items-center gap-0.5 rounded-full bg-white/[0.03] p-1 ring-1 ring-inset ring-white/[0.06]">
              {navItems.map((item) => {
                const active = item.isActive(location.pathname);
                return (
                  <li key={item.key}>
                    <Link
                      to={item.to}
                      aria-current={active ? 'page' : undefined}
                      className={cn(
                        'flex h-8 items-center gap-1.5 whitespace-nowrap rounded-full px-3.5 text-[13px] font-bold transition-colors',
                        'focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-300',
                        active
                          ? 'bg-white/[0.1] text-text-primary shadow-[inset_0_1px_0_0_rgba(255,255,255,0.08)]'
                          : 'text-text-secondary hover:text-text-primary'
                      )}
                    >
                      <Icon
                        name={item.icon}
                        size={15}
                        className={active ? 'text-brand-300' : 'opacity-70'}
                      />
                      {item.label}
                      <NavBadge label={item.badge} />
                    </Link>
                  </li>
                );
              })}
            </ul>
          </nav>

          {/* Right side: Play + wallet/auth + mobile menu */}
          <div className="ml-auto flex shrink-0 items-center gap-2 lg:ml-0">
            <a
              href={PLAY_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="hidden h-9 items-center gap-1.5 rounded-control px-3 text-[13px] font-bold text-text-secondary transition-colors hover:bg-white/[0.05] hover:text-text-primary lg:inline-flex"
            >
              <Icon name="play" size={13} />
              Play poker
              <span className="sr-only">(opens in a new tab)</span>
            </a>

            {hasSession ? (
              <>
                <NetworkPill />
                <WalletStatus
                  onOpenDrawer={handleOpenWalletDrawer}
                  isDrawerOpen={isWalletDrawerOpen}
                />
              </>
            ) : (
              <div className="flex items-center gap-2">
                <SignInButton size="sm" variant="grey-outlined" className="hidden sm:inline-flex" />
                <ConnectWalletButton size="sm" />
              </div>
            )}

            {/* Sign in prompt for wallet-only mode - hidden on mobile */}
            {isWalletOnlyMode && (
              <SignInButton size="sm" variant="ghost" className="hidden sm:inline-flex">
                Sign In
              </SignInButton>
            )}

            <MenuButton isOpen={isMobileMenuOpen} onClick={handleToggleMobileMenu} />
          </div>
        </div>
      </header>

      {/* Mobile menu drawer */}
      <MobileMenu
        isOpen={isMobileMenuOpen}
        onClose={handleCloseMobileMenu}
        isConnected={isConnected}
        isAuthenticated={isAuthenticated}
        navItems={navItems}
        email={email}
      />

      {/* Multi-wallet drawer */}
      <Suspense fallback={null}>
        <MultiWalletDrawer
          isOpen={isWalletDrawerOpen}
          onClose={handleCloseWalletDrawer}
          privyAddress={privyAddress}
          selectedAddress={selectedAddress}
          onSelectWallet={handleSelectWallet}
          initialView={drawerInitialView}
          email={email}
        />
      </Suspense>
    </>
  );
}
